import AsyncStorage from '@react-native-async-storage/async-storage';

class WebsiteAPI {
  constructor() {
    this.baseURL = 'https://api.jobeng.ai'; // Change this to your API URL
    this.timeout = 10000; // 10 seconds
  }

  // Helper method to get auth token
  async getAuthToken() {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  // Helper method to make API requests
  async makeRequest(endpoint, options = {}) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}${endpoint}`;
      
      const defaultHeaders = {
        'Content-Type': 'application/json',
      };

      if (token) {
        defaultHeaders.Authorization = `Bearer ${token}`;
      }

      const config = {
        timeout: this.timeout,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        ...options,
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  // Website Management APIs
  async getWebsites(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/websites${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  async getWebsite(websiteId) {
    return this.makeRequest(`/websites/${websiteId}`);
  }

  async createWebsite(websiteData) {
    return this.makeRequest('/websites', {
      method: 'POST',
      body: JSON.stringify(websiteData),
    });
  }

  async updateWebsite(websiteId, websiteData) {
    return this.makeRequest(`/websites/${websiteId}`, {
      method: 'PUT',
      body: JSON.stringify(websiteData),
    });
  }

  async deleteWebsite(websiteId) {
    return this.makeRequest(`/websites/${websiteId}`, {
      method: 'DELETE',
    });
  }

  async publishWebsite(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/publish`, {
      method: 'POST',
    });
  }

  // Content Management APIs
  async getContent(websiteId, contentType, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/websites/${websiteId}/content/${contentType}${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  async createContent(websiteId, contentType, contentData) {
    return this.makeRequest(`/websites/${websiteId}/content/${contentType}`, {
      method: 'POST',
      body: JSON.stringify(contentData),
    });
  }

  async updateContent(websiteId, contentType, contentId, contentData) {
    return this.makeRequest(`/websites/${websiteId}/content/${contentType}/${contentId}`, {
      method: 'PUT',
      body: JSON.stringify(contentData),
    });
  }

  async deleteContent(websiteId, contentType, contentId) {
    return this.makeRequest(`/websites/${websiteId}/content/${contentType}/${contentId}`, {
      method: 'DELETE',
    });
  }

  // Analytics APIs
  async getAnalytics(websiteId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/websites/${websiteId}/analytics${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  // Sync APIs
  async syncToWebsite(websiteId, syncData) {
    return this.makeRequest(`/websites/${websiteId}/sync`, {
      method: 'POST',
      body: JSON.stringify(syncData),
    });
  }

  async syncFromWebsite(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/sync`);
  }

  // Utility APIs
  async getTemplates() {
    return this.makeRequest('/templates');
  }

  async getDomainSuggestions(name) {
    return this.makeRequest('/domain-suggestions', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  // File Upload API
  async uploadFile(file, websiteId) {
    try {
      const formData = new FormData();
      formData.append('file', {
        uri: file.uri,
        type: file.type || 'image/jpeg',
        name: file.name || 'upload.jpg',
      });
      formData.append('website_id', websiteId.toString());

      const token = await this.getAuthToken();
      const headers = {};
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Batch Operations
  async batchUpdateContent(websiteId, updates) {
    return this.makeRequest(`/websites/${websiteId}/batch-update`, {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  }

  async batchDeleteContent(websiteId, contentIds) {
    return this.makeRequest(`/websites/${websiteId}/batch-delete`, {
      method: 'POST',
      body: JSON.stringify({ content_ids: contentIds }),
    });
  }

  // Search APIs
  async searchContent(websiteId, query, filters = {}) {
    const params = { query, ...filters };
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/websites/${websiteId}/search?${queryString}`);
  }

  // Export APIs
  async exportWebsite(websiteId, format = 'html') {
    return this.makeRequest(`/websites/${websiteId}/export?format=${format}`);
  }

  async exportContent(websiteId, contentType, format = 'json') {
    return this.makeRequest(`/websites/${websiteId}/content/${contentType}/export?format=${format}`);
  }

  // Backup and Restore APIs
  async createBackup(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/backup`, {
      method: 'POST',
    });
  }

  async restoreBackup(websiteId, backupId) {
    return this.makeRequest(`/websites/${websiteId}/restore`, {
      method: 'POST',
      body: JSON.stringify({ backup_id: backupId }),
    });
  }

  async getBackups(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/backups`);
  }

  // Performance APIs
  async getPerformanceMetrics(websiteId, timeRange = '7d') {
    return this.makeRequest(`/websites/${websiteId}/performance?range=${timeRange}`);
  }

  async runPerformanceTest(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/performance-test`, {
      method: 'POST',
    });
  }

  // SEO APIs
  async getSEOAnalysis(websiteId) {
    return this.makeRequest(`/websites/${websiteId}/seo-analysis`);
  }

  async updateSEOSettings(websiteId, seoData) {
    return this.makeRequest(`/websites/${websiteId}/seo`, {
      method: 'PUT',
      body: JSON.stringify(seoData),
    });
  }

  // Error Handling Helpers
  handleAPIError(error) {
    console.error('API Error:', error);
    
    if (error.message.includes('timeout')) {
      return {
        type: 'TIMEOUT',
        message: 'Request timed out. Please check your internet connection.',
      };
    }
    
    if (error.message.includes('401')) {
      return {
        type: 'UNAUTHORIZED',
        message: 'Authentication required. Please log in again.',
      };
    }
    
    if (error.message.includes('403')) {
      return {
        type: 'FORBIDDEN',
        message: 'You do not have permission to perform this action.',
      };
    }
    
    if (error.message.includes('404')) {
      return {
        type: 'NOT_FOUND',
        message: 'The requested resource was not found.',
      };
    }
    
    if (error.message.includes('500')) {
      return {
        type: 'SERVER_ERROR',
        message: 'Server error. Please try again later.',
      };
    }
    
    return {
      type: 'UNKNOWN',
      message: error.message || 'An unexpected error occurred.',
    };
  }

  // Cache Management
  async clearCache() {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const cacheKeys = keys.filter(key => key.startsWith('website_cache_'));
      await AsyncStorage.multiRemove(cacheKeys);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  async getCachedData(key) {
    try {
      const cachedData = await AsyncStorage.getItem(`website_cache_${key}`);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const now = Date.now();
        
        // Check if cache is still valid (5 minutes)
        if (now - parsed.timestamp < 5 * 60 * 1000) {
          return parsed.data;
        } else {
          // Remove expired cache
          await AsyncStorage.removeItem(`website_cache_${key}`);
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting cached data:', error);
      return null;
    }
  }

  async setCachedData(key, data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(`website_cache_${key}`, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting cached data:', error);
    }
  }

  // Network Status Check
  async checkNetworkStatus() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'HEAD',
        timeout: 5000,
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

// Create and export a singleton instance
const websiteAPI = new WebsiteAPI();
export default websiteAPI;
